import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Space,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Radio,
  Tag,
  Upload,
  Result,
  Spin,
  Grid,
  Divider
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  CarOutlined,
  ArrowRightOutlined,
  InboxOutlined
} from "@ant-design/icons";
import moment from "moment";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { MapPin, Plane, Hotel, Layers, Hash, User, ShieldCheck, Zap, Star, Sparkles, AlertCircle } from "lucide-react";
import logo from "../../assets/images/hotal-management-logo.png";
import apiPath from "../../constants/apiPath";
import useRequest from "../../hooks/useRequest";
import { AuthContext } from "../../context/AuthContext";
import { Severty, ShowToast } from "../../helper/toast";

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const RegistrationCard = () => {
  const { request } = useRequest();
  const { userProfile } = useContext(AuthContext);
  const screens = useBreakpoint();

  const authRole = localStorage.getItem("authRole") || "admin";

  // Retrieve airline ID from profile if logged in as airline
  const currentAirlineId = useMemo(() => {
    if (authRole === "airline" && userProfile) {
      return userProfile._id || userProfile.id || "";
    }
    return "";
  }, [authRole, userProfile]);

  const currentAirlineName = useMemo(() => {
    if (authRole === "airline" && userProfile) {
      return userProfile.name || "My Airline";
    }
    return "";
  }, [authRole, userProfile]);

  // State Management
  const [airlines, setAirlines] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loadingHotels, setLoadingHotels] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // { success: boolean, bookingId?: string }

  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    airline_id: currentAirlineId,
    hotel_id: "",
    checkIn: null,
    checkOut: null,
    room_type: "",
    number_of_rooms: 1,
    e_ticket: null,
    transport_required: false,
    trip_type: null,
    passengers: [],
    boarding_passes: []
  });

  const [errors, setErrors] = useState({});

  // Reset form helper
  const handleResetForm = () => {
    setFormData({
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      airline_id: currentAirlineId,
      hotel_id: "",
      checkIn: null,
      checkOut: null,
      room_type: "",
      number_of_rooms: 1,
      e_ticket: null,
      transport_required: false,
      trip_type: null,
      passengers: [],
      boarding_passes: []
    });
    setErrors({});
    setSubmitStatus(null);
    if (authRole === "admin") {
      setHotels([]);
    }
  };

  // Fetch airlines for Admin role
  useEffect(() => {
    if (authRole === "admin") {
      request({
        url: "/airline/users",
        method: "GET",
        onSuccess: (res) => {
          if (res?.success && Array.isArray(res.users)) {
            setAirlines(res.users);
          }
        },
        onError: () => setAirlines([])
      });
    }
  }, [authRole, request]);

  // Fetch hotels associated with selected airline
  useEffect(() => {
    const airlineIdToFetch = formData.airline_id;
    if (!airlineIdToFetch) {
      setHotels([]);
      return;
    }

    setLoadingHotels(true);
    request({
      url: `/guest/hotels/${airlineIdToFetch}`,
      method: "GET",
      onSuccess: (res) => {
        if (res?.success && Array.isArray(res.hotels)) {
          setHotels(res.hotels);
        } else {
          setHotels([]);
        }
        setLoadingHotels(false);
      },
      onError: () => {
        setHotels([]);
        setLoadingHotels(false);
      }
    });
  }, [formData.airline_id, request]);

  // Synchronize airline ID if role is airline
  useEffect(() => {
    if (currentAirlineId) {
      setFormData(prev => ({ ...prev, airline_id: currentAirlineId }));
    }
  }, [currentAirlineId]);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Find selected hotel details
  const selectedHotel = useMemo(() => {
    return hotels.find(h => h._id === formData.hotel_id || h.Hotel_Id === formData.hotel_id);
  }, [hotels, formData.hotel_id]);

  const hasBusCompany = useMemo(() => {
    return selectedHotel ? !!selectedHotel.has_bus_company : false;
  }, [selectedHotel]);

  // Reset transport option if selected hotel has no linked bus company
  useEffect(() => {
    if (!hasBusCompany) {
      if (formData.transport_required || formData.trip_type !== null || (formData.passengers && formData.passengers.length > 0)) {
        setFormData(prev => ({
          ...prev,
          transport_required: false,
          trip_type: null,
          passengers: []
        }));
      }
    }
  }, [hasBusCompany, formData.transport_required, formData.trip_type, formData.passengers]);

  // Filter available rooms in selected hotel matching the airline
  const availableRooms = useMemo(() => {
    if (!selectedHotel) return [];

    // The guest API `/guest/hotels/:airline_id` already filters rooms on the backend.
    // However, to be absolutely robust, we can double check or map them.
    return (selectedHotel.rooms || []).filter(room => room.status === 1 && room.allocation_status === "active");
  }, [selectedHotel]);

  // Find selected room category details
  const selectedRoomCategory = useMemo(() => {
    return availableRooms.find(r => r.type === formData.room_type);
  }, [availableRooms, formData.room_type]);

  // Available meals based on hotel configuration
  const hotelMeals = useMemo(() => {
    if (!selectedHotel) return [];
    return (selectedHotel.meals || []).filter(meal => meal.available === true);
  }, [selectedHotel]);

  const handleDateChange = (date, field) => {
    handleChange(field, date);
  };

  const calculateTotalDays = () => {
    if (formData.checkIn && formData.checkOut) {
      const start = moment(formData.checkIn);
      const end = moment(formData.checkOut);
      return end.diff(start, "days");
    }
    return 0;
  };

  // Form Validation and Submission
  const handleSubmit = () => {
    const newErrors = {};

    if (!formData.customer_name?.trim()) newErrors.customer_name = "Name is required";
    if (!formData.customer_email?.trim()) {
      newErrors.customer_email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.customer_email)) {
      newErrors.customer_email = "Email is invalid";
    }

    if (!formData.customer_phone?.trim()) newErrors.customer_phone = "Phone is required";
    if (!formData.airline_id) newErrors.airline_id = "Airline selection is required";
    if (!formData.hotel_id) newErrors.hotel_id = "Hotel selection is required";
    if (!formData.checkIn) newErrors.checkIn = "Check-in date is required";
    if (!formData.checkOut) newErrors.checkOut = "Check-out date is required";
    if (formData.checkIn && formData.checkOut && formData.checkOut.isSameOrBefore(formData.checkIn, "day")) {
      newErrors.checkOut = "Check-out must be after check-in";
    }
    if (!formData.room_type) newErrors.room_type = "Room type is required";

    if (selectedRoomCategory && formData.number_of_rooms > selectedRoomCategory.available_rooms) {
      newErrors.number_of_rooms = `Only ${selectedRoomCategory.available_rooms} rooms available`;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      ShowToast("Please provide all required information", Severty.ERROR);
      return;
    }

    // Validate transport passengers if selected
    if (formData.transport_required) {
      for (let i = 0; i < formData.passengers.length; i++) {
        if (!formData.passengers[i].name?.trim()) {
          ShowToast(`Please enter a name for Passenger #${i + 1}`, Severty.ERROR);
          return;
        }
      }
    }

    // Validate boarding passes if added
    if (formData.boarding_passes && formData.boarding_passes.length > 0) {
      for (let i = 0; i < formData.boarding_passes.length; i++) {
        const bp = formData.boarding_passes[i];
        if (!bp.pnr || !bp.pnr.trim()) {
          ShowToast(`Please enter PNR for Boarding Pass #${i + 1}`, Severty.ERROR);
          return;
        }
        if (!bp.file) {
          ShowToast(`Please upload an image for Boarding Pass #${i + 1}`, Severty.ERROR);
          return;
        }
      }
    }

    setIsSubmitting(true);

    const boardingPassesMetadata = (formData.boarding_passes || []).map((bp) => ({
      pnr: bp.pnr,
      has_file: !!bp.file
    }));

    const submissionData = {
      customer_name: formData.customer_name,
      customer_email: formData.customer_email,
      customer_phone: formData.customer_phone,
      airline_id: formData.airline_id,
      hotel_id: selectedHotel._id, // use MongoDB ObjectID
      checkIn: formData.checkIn.format("YYYY-MM-DD"),
      checkOut: formData.checkOut.format("YYYY-MM-DD"),
      adults: 1,
      children: 0,
      room_type: formData.room_type,
      number_of_rooms: formData.number_of_rooms,
      transport_required: formData.transport_required,
      trip_type: formData.trip_type,
      passengers: formData.passengers,
      e_ticket: formData.e_ticket,
      boarding_passes: boardingPassesMetadata
    };

    // If extra services/meals are available, automatically map them as pre-agreed
    let specialRequestParts = [];
    const mealPlanDetails = {};

    if (hotelMeals.length > 0) {
      const selectedMeals = [];
      const mealsObj = {};
      hotelMeals.forEach(m => {
        const mealKey = m.meal_name.toLowerCase();
        mealsObj[mealKey] = true;
        selectedMeals.push(m.meal_name);
      });
      specialRequestParts.push(`Meals Included: ${selectedMeals.join(", ")}`);

      submissionData.extra_services = {
        bedrooms: 1,
        bathrooms: 1,
        meals: mealsObj,
        additional_services: { gym_access: false, wifi_premium: false, parking: false }
      };
      submissionData.need_extra_services = true;
    }

    submissionData.special_request = specialRequestParts.join(". ");

    // Handle pickup & dropoff payloads
    submissionData.pickup_service = {
      need_service: formData.trip_type === "one_way" || formData.trip_type === "round_trip",
      details: {
        airline_number: "",
        airline_name: authRole === "airline" ? currentAirlineName : (airlines.find(a => a._id === formData.airline_id)?.name || ""),
        airport_name: ""
      }
    };

    submissionData.dropoff_service = {
      need_service: formData.trip_type === "round_trip",
      details: {
        terminal: "",
        dropoff_point: "",
        airport_name: ""
      }
    };

    // Construct FormData for multipart submission
    const data = new FormData();
    Object.entries(submissionData).forEach(([key, value]) => {
      if (value && typeof value === "object" && !(value instanceof File)) {
        data.append(key, JSON.stringify(value));
      } else if (value !== null && value !== undefined) {
        data.append(key, value);
      }
    });

    // Append boarding pass image files
    (formData.boarding_passes || []).forEach((bp) => {
      if (bp.file) {
        data.append("boarding_passes_files", bp.file);
      }
    });

    request({
      url: "/guest/create-booking",
      method: "POST",
      data: data,
      onSuccess: (res) => {
        setIsSubmitting(false);
        if (res?.success) {
          ShowToast("Booking created successfully!", Severty.SUCCESS);
          setSubmitStatus({
            success: true,
            bookingId: res.booking?.Booking_Id || "CONFIRMED"
          });
        } else {
          ShowToast(res?.message || "Failed to create booking", Severty.ERROR);
        }
      },
      onError: (err) => {
        setIsSubmitting(false);
        const msg = err?.response?.data?.message || "Error submitting booking enquiry";
        ShowToast(msg, Severty.ERROR);
      }
    });
  };

  if (submitStatus?.success) {
    return (
      <div style={{ padding: "40px 16px", minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center", background: "#FAF7F2" }}>
        <Card
          bordered={false}
          style={{
            width: "100%",
            maxWidth: 600,
            borderRadius: 32,
            boxShadow: "0 8px 32px rgba(19, 34, 114, 0.06)",
            textAlign: "center",
            padding: screens.xs ? "20px 12px" : "40px 24px"
          }}
        >
          <Result
            status="success"
            title={
              <Title level={2} style={{ color: "#ca8a04", margin: 0, fontWeight: 700 }}>
                Reservation Submitted!
              </Title>
            }
            subTitle={
              <div style={{ marginTop: 8 }}>
                <Text style={{ fontSize: 16 }}>
                  Booking request was processed successfully.
                </Text>
                <div style={{ marginTop: 12 }}>
                  <Tag color="gold" style={{ fontSize: 14, padding: "4px 12px", borderRadius: 6 }}>
                    Booking ID: {submitStatus.bookingId}
                  </Tag>
                </div>
              </div>
            }
            extra={[
              <Button
                type="primary"
                key="register-new"
                size="large"
                icon={<ArrowRightOutlined />}
                onClick={handleResetForm}
                style={{
                  borderRadius: 12,
                  height: 52,
                  background: "#ca8a04",
                  borderColor: "#ca8a04",
                  fontWeight: 700,
                  boxShadow: "0 6px 16px rgba(202, 138, 4, 0.2)"
                }}
              >
                Register Another Passenger
              </Button>
            ]}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="registration-card-page" style={{ padding: screens.xs ? "16px 12px" : "32px 24px", minHeight: "100vh", background: "linear-gradient(135deg, #FAF7F2 0%, #F5EFEB 100%)" }}>

      {/* Title Header */}
      <div style={{ textAlign: "center", marginBottom: 32, paddingTop: 16 }}>
        {/* <div 
          style={{ 
            display: "inline-flex", 
            alignItems: "center", 
            gap: 8, 
            padding: "6px 16px", 
            borderRadius: 999, 
            background: "#FFF9F0", 
            border: "1px solid #FEF08A",
            marginBottom: 16
          }}
        >
          <Hotel size={14} style={{ color: "#ca8a04" }} />
          <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#ca8a04" }}>
            Hotel Reservation
          </span>
        </div> */}
        <Title level={1} style={{ fontSize: screens.xs ? 24 : 36, fontWeight: 800, margin: "0 0 12px" }}>
          <span style={{ color: "#ca8a04" }}>Passanger hotel reservation</span>
        </Title>
        {/* <Text type="secondary" style={{ fontSize: screens.xs ? 13 : 15, display: "block", maxWidth: 520, margin: "0 auto", lineHeight: 1.6 }}>
          Share a few details and we'll confirm your stay within minutes. Your information stays private and secure.
        </Text> */}
      </div>

      <div style={{ maxWidth: 840, margin: "0 auto" }}>

        {/* Card 1: Personal Information */}
        <Card
          bordered={false}
          style={{
            borderRadius: 24,
            boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
            marginBottom: 24,
            overflow: "visible"
          }}
          bodyStyle={{ padding: screens.xs ? 20 : 32 }}
        >
          <Space align="start" size={14} style={{ marginBottom: 28 }}>
            <div
              style={{
                width: 40,
                height: 40,
                background: "#FFF9F0",
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ca8a04"
              }}
            >
              <User size={20} />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, fontWeight: 700 }}>Personal Information</Title>
              <Text type="secondary" style={{ fontSize: 12 }}>Tell us who's traveling</Text>
            </div>
          </Space>

          <Row gutter={[24, 20]}>
            <Col xs={24} md={12}>
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                <Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c", letterSpacing: "0.05em" }}>Full Name *</Text>
                <Input
                  size="large"
                  placeholder="Enter passenger name"
                  prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                  value={formData.customer_name}
                  onChange={(e) => handleChange("customer_name", e.target.value)}
                  status={errors.customer_name ? "error" : ""}
                  style={{ borderRadius: 12, height: 48 }}
                />
                {errors.customer_name && <Text type="danger" style={{ fontSize: 11 }}>{errors.customer_name}</Text>}
              </Space>
            </Col>

            <Col xs={24} md={12}>
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                <Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c", letterSpacing: "0.05em" }}>Email Address *</Text>
                <Input
                  size="large"
                  placeholder="your.email@example.com"
                  prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                  value={formData.customer_email}
                  onChange={(e) => handleChange("customer_email", e.target.value)}
                  status={errors.customer_email ? "error" : ""}
                  style={{ borderRadius: 12, height: 48 }}
                />
                {errors.customer_email && <Text type="danger" style={{ fontSize: 11 }}>{errors.customer_email}</Text>}
              </Space>
            </Col>

            <Col xs={24} md={12}>
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                <Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c", letterSpacing: "0.05em" }}>Phone Number *</Text>
                <PhoneInput
                  country={"us"}
                  value={formData.customer_phone}
                  onChange={(val) => handleChange("customer_phone", val ? `+${val}` : "")}
                  containerStyle={{ width: "100%" }}
                  inputStyle={{
                    width: "100%",
                    height: "48px",
                    borderRadius: "12px",
                    fontSize: "14px",
                    paddingLeft: "48px",
                    backgroundColor: "#ffffff",
                    border: errors.customer_phone ? "1px solid #ff4d4f" : "1px solid #d9d9d9"
                  }}
                  buttonStyle={{
                    borderRadius: "12px 0 0 12px",
                    backgroundColor: "#ffffff",
                    border: errors.customer_phone ? "1px solid #ff4d4f" : "1px solid #d9d9d9",
                    borderRight: "none",
                    paddingLeft: "5px"
                  }}
                  placeholder="Enter phone number"
                />
                {errors.customer_phone && <Text type="danger" style={{ fontSize: 11 }}>{errors.customer_phone}</Text>}
              </Space>
            </Col>

            <Col xs={24} md={12}>
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                <Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c", letterSpacing: "0.05em" }}>Attach E-Ticket</Text>
                <Upload
                  maxCount={1}
                  beforeUpload={(file) => {
                    handleChange("e_ticket", file);
                    return false;
                  }}
                  onRemove={() => handleChange("e_ticket", null)}
                  showUploadList={true}
                >
                  <Button
                    icon={<UploadOutlined />}
                    size="large"
                    style={{
                      width: "100%",
                      borderRadius: 12,
                      height: 48,
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      color: "#666"
                    }}
                  >
                    <span>{formData.e_ticket ? formData.e_ticket.name : "Click to upload E-Ticket"}</span>
                    <span
                      style={{
                        background: "#f0f0f0",
                        padding: "2px 10px",
                        borderRadius: 6,
                        fontSize: 11,
                        color: "#333",
                        fontWeight: 600
                      }}
                    >
                      BROWSE
                    </span>
                  </Button>
                </Upload>
                {!formData.e_ticket && <Text type="secondary" style={{ fontSize: 10 }}>PDF, PNG, JPG • MAX 5MB</Text>}
              </Space>
            </Col>
          </Row>

          <Divider style={{ margin: "24px 0" }} />

          <div style={{ marginBottom: 16 }}>
            <Title level={5} style={{ margin: 0, fontWeight: 700 }}>PNR & Boarding Pass Details</Title>
            <Text type="secondary" style={{ fontSize: 12 }}>Add passenger PNRs and upload boarding pass images</Text>
          </div>

          {(formData.boarding_passes || []).map((bp, index) => (
            <Card
              key={index}
              size="small"
              style={{
                marginBottom: 16,
                borderRadius: 16,
                background: "#fafafa",
                border: "1px solid #e8e8e8",
                boxShadow: "0 2px 8px rgba(0,0,0,0.01)"
              }}
            >
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={10}>
                  <Space direction="vertical" style={{ width: "100%" }} size={4}>
                    <Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c", letterSpacing: "0.05em" }}>PNR *</Text>
                    <Input
                      placeholder="Enter PNR code"
                      value={bp.pnr}
                      onChange={(e) => {
                        const pnrVal = e.target.value;
                        setFormData(prev => {
                          const newBP = prev.boarding_passes.map((item, idx) =>
                            idx === index ? { ...item, pnr: pnrVal } : item
                          );
                          return { ...prev, boarding_passes: newBP };
                        });
                      }}
                      style={{ borderRadius: 8 }}
                    />
                  </Space>
                </Col>
                <Col xs={24} md={10}>
                  <Space direction="vertical" style={{ width: "100%" }} size={4}>
                    <Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c", letterSpacing: "0.05em" }}>Boarding Pass Image *</Text>
                    <Upload
                      maxCount={1}
                      beforeUpload={(file) => {
                        setFormData(prev => {
                          const newBP = prev.boarding_passes.map((item, idx) =>
                            idx === index ? { ...item, file: file } : item
                          );
                          return { ...prev, boarding_passes: newBP };
                        });
                        return false;
                      }}
                      showUploadList={true}
                      fileList={bp.file ? [{ uid: `-bp-${index}`, name: bp.file.name, originFileObj: bp.file }] : []}
                      onRemove={() => {
                        setFormData(prev => {
                          const newBP = prev.boarding_passes.map((item, idx) =>
                            idx === index ? { ...item, file: null } : item
                          );
                          return { ...prev, boarding_passes: newBP };
                        });
                      }}
                    >
                      <Input
                        readOnly
                        placeholder={bp.file ? bp.file.name : "Click to upload image"}
                        prefix={<UploadOutlined style={{ color: "#bfbfbf" }} />}
                        style={{ cursor: "pointer", borderRadius: 8 }}
                      />
                    </Upload>
                  </Space>
                </Col>
                <Col xs={24} md={4} style={{ textAlign: "center", paddingTop: 16 }}>
                  <Button
                    danger
                    type="text"
                    onClick={() => {
                      setFormData(prev => {
                        const newBP = prev.boarding_passes.filter((_, idx) => idx !== index);
                        return { ...prev, boarding_passes: newBP };
                      });
                    }}
                    style={{ fontWeight: "500" }}
                  >
                    Remove
                  </Button>
                </Col>
              </Row>
            </Card>
          ))}

          <Button
            type="dashed"
            onClick={() => {
              setFormData(prev => ({
                ...prev,
                boarding_passes: [...(prev.boarding_passes || []), { pnr: "", file: null }]
              }));
            }}
            style={{ width: "100%", borderRadius: 12, height: 42, marginTop: 8, fontWeight: "500" }}
          >
            + Add PNR & Boarding Pass
          </Button>
        </Card>

        {/* Card 2: Travel Details */}
        <Card
          bordered={false}
          style={{
            borderRadius: 24,
            boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
            marginBottom: 24,
            overflow: "visible"
          }}
          bodyStyle={{ padding: screens.xs ? 20 : 32 }}
        >
          <Space align="start" size={14} style={{ marginBottom: 28 }}>
            <div
              style={{
                width: 40,
                height: 40,
                background: "#FFF9F0",
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ca8a04"
              }}
            >
              <MapPin size={20} />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, fontWeight: 700 }}>Travel Details</Title>
              <Text type="secondary" style={{ fontSize: 12 }}>Where and when are you staying</Text>
            </div>
          </Space>

          <Row gutter={[24, 20]}>
            <Col xs={24} md={12}>
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                <Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c", letterSpacing: "0.05em" }}>
                  {authRole === "airline" ? "Your Airline *" : "Select Airline *"}
                </Text>
                {authRole === "airline" ? (
                  <Input
                    size="large"
                    value={currentAirlineName}
                    readOnly
                    disabled
                    prefix={<Plane size={14} style={{ color: "#bfbfbf", marginRight: 4 }} />}
                    style={{ borderRadius: 12, height: 48, background: "#f5f5f5" }}
                  />
                ) : (
                  <Select
                    size="large"
                    placeholder="Choose an airline"
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    value={formData.airline_id || undefined}
                    onChange={(val) => {
                      handleChange("airline_id", val);
                      setFormData(prev => ({ ...prev, hotel_id: "", room_type: "", number_of_rooms: 1 }));
                    }}
                    status={errors.airline_id ? "error" : ""}
                    style={{ width: "100%", height: 48 }}
                    dropdownStyle={{ borderRadius: 12 }}
                  >
                    {airlines.map(a => (
                      <Option key={a._id} value={a._id}>{a.name}</Option>
                    ))}
                  </Select>
                )}
                {errors.airline_id && <Text type="danger" style={{ fontSize: 11 }}>{errors.airline_id}</Text>}
              </Space>
            </Col>

            <Col xs={24} md={12}>
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                <Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c", letterSpacing: "0.05em" }}>Select Hotel *</Text>
                <Select
                  size="large"
                  placeholder="Choose a hotel"
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  value={formData.hotel_id || undefined}
                  onChange={(val) => {
                    handleChange("hotel_id", val);
                    setFormData(prev => ({ ...prev, room_type: "", number_of_rooms: 1 }));
                  }}
                  disabled={!formData.airline_id || loadingHotels}
                  loading={loadingHotels}
                  status={errors.hotel_id ? "error" : ""}
                  style={{ width: "100%" }}
                  dropdownStyle={{ borderRadius: 12 }}
                >
                  {hotels.map(h => (
                    <Option key={h._id} value={h.Hotel_Id}>{h.Hotel_name}</Option>
                  ))}
                </Select>
                {errors.hotel_id && <Text type="danger" style={{ fontSize: 11 }}>{errors.hotel_id}</Text>}
              </Space>
            </Col>

            <Col xs={24} md={12}>
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                <Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c", letterSpacing: "0.05em" }}>Check-in Date *</Text>
                <DatePicker
                  size="large"
                  format="DD / MM / YYYY"
                  placeholder="dd / mm / yyyy"
                  value={formData.checkIn}
                  onChange={(date) => handleDateChange(date, "checkIn")}
                  disabledDate={(curr) => curr && curr.isBefore(moment().startOf("day"))}
                  status={errors.checkIn ? "error" : ""}
                  style={{ width: "100%", height: 48, borderRadius: 12 }}
                />
                {errors.checkIn && <Text type="danger" style={{ fontSize: 11 }}>{errors.checkIn}</Text>}
              </Space>
            </Col>

            <Col xs={24} md={12}>
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                <Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c", letterSpacing: "0.05em" }}>Check-out Date *</Text>
                <DatePicker
                  size="large"
                  format="DD / MM / YYYY"
                  placeholder="dd / mm / yyyy"
                  value={formData.checkOut}
                  onChange={(date) => handleDateChange(date, "checkOut")}
                  disabledDate={(curr) => {
                    if (!formData.checkIn) return curr && curr.isBefore(moment().startOf("day"));
                    return curr && curr.isBefore(formData.checkIn.clone().add(1, "day").startOf("day"));
                  }}
                  status={errors.checkOut ? "error" : ""}
                  style={{ width: "100%", height: 48, borderRadius: 12 }}
                />
                {errors.checkOut && <Text type="danger" style={{ fontSize: 11 }}>{errors.checkOut}</Text>}
              </Space>
            </Col>

            <Col xs={24} md={12}>
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                <Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c", letterSpacing: "0.05em" }}>Room Type *</Text>
                <Select
                  size="large"
                  placeholder="Choose room type"
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  value={formData.room_type || undefined}
                  onChange={(val) => {
                    handleChange("room_type", val);
                    setFormData(prev => ({ ...prev, number_of_rooms: 1 }));
                  }}
                  disabled={!formData.hotel_id}
                  status={errors.room_type ? "error" : ""}
                  style={{ width: "100%" }}
                  dropdownStyle={{ borderRadius: 12 }}
                >
                  {availableRooms.map(room => (
                    <Option key={room._id} value={room.type}>
                      {room.type} ({room.available_rooms} Available)
                    </Option>
                  ))}
                </Select>
                {errors.room_type && <Text type="danger" style={{ fontSize: 11 }}>{errors.room_type}</Text>}
              </Space>
            </Col>

            <Col xs={24} md={12}>
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                <Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c", letterSpacing: "0.05em" }}>Number of Rooms *</Text>
                <InputNumber
                  size="large"
                  min={1}
                  max={selectedRoomCategory?.available_rooms || 1}
                  value={formData.number_of_rooms}
                  onChange={(val) => handleChange("number_of_rooms", val || 1)}
                  disabled={!formData.room_type}
                  style={{ width: "100%", height: 48, borderRadius: 12, paddingTop: 4 }}
                />
                {selectedRoomCategory && (
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    Max available: {selectedRoomCategory.available_rooms}
                  </Text>
                )}
                {errors.number_of_rooms && <Text type="danger" style={{ fontSize: 11 }}>{errors.number_of_rooms}</Text>}
              </Space>
            </Col>
          </Row>

          {calculateTotalDays() > 0 && (
            <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
              <div
                style={{
                  padding: "8px 16px",
                  background: "#FFFDF0",
                  border: "1px solid #FEF08A",
                  borderRadius: 10
                }}
              >
                <Text strong style={{ color: "#ca8a04", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Total Duration: <span style={{ color: "#1a1a1a", marginLeft: 4 }}>{calculateTotalDays()} {calculateTotalDays() === 1 ? "Day" : "Days"}</span>
                </Text>
              </div>
            </div>
          )}
        </Card>

        {/* Card 3: Hotel Meal Plans */}
        {formData.hotel_id && hotelMeals.length > 0 && (
          <Card
            bordered={false}
            style={{
              borderRadius: 24,
              boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
              marginBottom: 24,
              background: "linear-gradient(135deg, #FFFDF5 0%, #ffffff 100%)",
              border: "1px solid #FEF08A"
            }}
            bodyStyle={{ padding: screens.xs ? 20 : 32 }}
          >
            <Space align="start" size={12} style={{ marginBottom: 16 }}>
              <Sparkles size={18} style={{ color: "#ca8a04" }} />
              <Title level={5} style={{ margin: 0, color: "#854d0e", fontWeight: 700 }}>Hotel Meal Plans</Title>
            </Space>

            <Text type="secondary" style={{ display: "block", marginBottom: 20, fontSize: 13 }}>
              The following meal plans are pre-agreed with the hotel and included in your stay.
            </Text>

            <Space size={12} wrap>
              {hotelMeals.map((meal) => (
                <Tag
                  key={meal._id}
                  icon={<CheckCircleOutlined style={{ color: "#ca8a04" }} />}
                  color="warning"
                  style={{
                    padding: "8px 20px",
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#ca8a04",
                    background: "#FFFDF2",
                    border: "1px solid #FEF08A"
                  }}
                >
                  {meal.meal_name}
                </Tag>
              ))}
            </Space>

            <div style={{ marginTop: 20, padding: 12, background: "rgba(202, 138, 4, 0.04)", borderRadius: 10 }}>
              <Text style={{ fontSize: 12, color: "#854d0e", display: "flex", alignItems: "center", gap: 6 }}>
                <CheckCircleOutlined style={{ fontSize: 12, color: "#ca8a04" }} />
                These meals are automatically included based on the hotel's agreement.
              </Text>
            </div>
          </Card>
        )}

        {/* Card 4: Transport to Hotel */}
        <Card
          bordered={false}
          style={{
            borderRadius: 24,
            boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
            marginBottom: 32
          }}
          bodyStyle={{ padding: screens.xs ? 20 : 32 }}
        >
          <Space align="start" size={14} style={{ marginBottom: 24 }}>
            <div
              style={{
                width: 40,
                height: 40,
                background: "#FFF9F0",
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ca8a04"
              }}
            >
              <CarOutlined style={{ fontSize: 20 }} />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, fontWeight: 700 }}>Transport to Hotel</Title>
              <Text type="secondary" style={{ fontSize: 12 }}>Do you require transport from the airport to the hotel?</Text>
            </div>
          </Space>

          <Radio.Group
            onChange={(e) => {
              const val = e.target.value;
              setFormData(prev => ({
                ...prev,
                transport_required: val !== "none",
                trip_type: val === "none" ? null : val
              }));
            }}
            value={formData.trip_type || "none"}
            style={{ width: "100%" }}
          >
            <Row gutter={[16, 16]}>
              {[
                { value: "none", label: "No transport needed" },
                { value: "one_way", label: "One-way (Airport → Hotel)" },
                { value: "round_trip", label: "Round-trip (Airport → Hotel & Hotel → Airport)" }
              ].map(opt => (
                <Col xs={24} md={8} key={opt.value}>
                  <Radio.Button
                    value={opt.value}
                    disabled={opt.value !== "none" && !hasBusCompany}
                    style={{
                      width: "100%",
                      height: "auto",
                      padding: "16px 12px",
                      borderRadius: 12,
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 600,
                      border: "1px solid #e8e8e8",
                      background: opt.value !== "none" && !hasBusCompany
                        ? "#f5f5f5"
                        : (formData.trip_type === opt.value || (opt.value === "none" && !formData.trip_type) ? "#FFFDF2" : "#ffffff"),
                      color: opt.value !== "none" && !hasBusCompany
                        ? "rgba(0, 0, 0, 0.25)"
                        : (formData.trip_type === opt.value || (opt.value === "none" && !formData.trip_type) ? "#ca8a04" : "#666"),
                      borderColor: opt.value !== "none" && !hasBusCompany
                        ? "#d9d9d9"
                        : (formData.trip_type === opt.value || (opt.value === "none" && !formData.trip_type) ? "#FEF08A" : "#e8e8e8")
                    }}
                  >
                    {opt.label}
                  </Radio.Button>
                </Col>
              ))}
            </Row>
          </Radio.Group>

          {!hasBusCompany && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: '#fffbe6',
              border: '1px solid #ffe58f',
              borderRadius: '12px',
              padding: '12px 16px',
              marginTop: '20px'
            }}>
              <AlertCircle size={18} style={{ color: '#d46b08', flexShrink: 0 }} />
              <Text style={{ fontSize: '13px', color: '#d46b08', fontWeight: '500', margin: 0 }}>
                {formData.hotel_id
                  ? "Transport options are disabled because there is no bus company linked to the selected hotel."
                  : "Please select a hotel to verify available transport options."}
              </Text>
            </div>
          )}

          {hasBusCompany && selectedHotel?.bus_company_name && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: '12px',
              padding: '12px 16px',
              marginTop: '20px'
            }}>
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px', flexShrink: 0 }} />
              <Text style={{ fontSize: '13px', color: '#389e0d', fontWeight: '500', margin: 0 }}>
                Transport service is provided by <Text strong style={{ color: '#237804' }}>{selectedHotel.bus_company_name}</Text>.
              </Text>
            </div>
          )}

          {formData.transport_required && (
            <div style={{ marginTop: 28, borderTop: "1px solid #f0f0f0", paddingTop: 24 }}>
              <Title level={5} style={{ marginBottom: 6, fontWeight: 700 }}>Additional Passenger Details</Title>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 20 }}>
                Add details of other passengers who will travel with you in the transport.
              </Text>

              {(formData.passengers || []).map((passenger, index) => (
                <Card
                  key={index}
                  size="small"
                  style={{ marginBottom: 16, borderRadius: 16, background: "#fafafa", border: "1px solid #e8e8e8" }}
                  bodyStyle={{ padding: 16 }}
                >
                  <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={7}>
                      <Text strong style={{ fontSize: 11, display: "block", marginBottom: 4, color: "#666" }}>Name *</Text>
                      <Input
                        placeholder="Passenger Name"
                        value={passenger.name}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData(prev => {
                            const list = prev.passengers.map((p, idx) => idx === index ? { ...p, name: val } : p);
                            return { ...prev, passengers: list };
                          });
                        }}
                        style={{ borderRadius: 8 }}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong style={{ fontSize: 11, display: "block", marginBottom: 4, color: "#666" }}>Email</Text>
                      <Input
                        placeholder="Passenger Email"
                        type="email"
                        value={passenger.email}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData(prev => {
                            const list = prev.passengers.map((p, idx) => idx === index ? { ...p, email: val } : p);
                            return { ...prev, passengers: list };
                          });
                        }}
                        style={{ borderRadius: 8 }}
                      />
                    </Col>
                    <Col xs={24} md={7}>
                      <Text strong style={{ fontSize: 11, display: "block", marginBottom: 4, color: "#666" }}>Phone Number</Text>
                      <PhoneInput
                        country={"us"}
                        value={passenger.phone}
                        onChange={(val) => {
                          setFormData(prev => {
                            const list = prev.passengers.map((p, idx) => idx === index ? { ...p, phone: val ? `+${val}` : "" } : p);
                            return { ...prev, passengers: list };
                          });
                        }}
                        containerStyle={{ width: "100%" }}
                        inputStyle={{
                          width: "100%",
                          height: "32px",
                          borderRadius: "8px",
                          fontSize: "13px",
                          paddingLeft: "48px",
                          backgroundColor: "#ffffff",
                          border: "1px solid #d9d9d9"
                        }}
                        buttonStyle={{
                          borderRadius: "8px 0 0 8px",
                          backgroundColor: "#ffffff",
                          border: "1px solid #d9d9d9",
                          borderRight: "none",
                          paddingLeft: "2px"
                        }}
                        placeholder="Enter phone"
                      />
                    </Col>
                    <Col xs={24} md={2} style={{ textAlign: "center" }}>
                      <Button
                        danger
                        type="text"
                        onClick={() => {
                          setFormData(prev => {
                            const list = prev.passengers.filter((_, idx) => idx !== index);
                            return { ...prev, passengers: list };
                          });
                        }}
                        style={{ fontWeight: 600 }}
                      >
                        Remove
                      </Button>
                    </Col>
                  </Row>
                </Card>
              ))}

              <Button
                type="dashed"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    passengers: [...prev.passengers, { name: "", email: "", phone: "" }]
                  }));
                }}
                style={{ width: "100%", borderRadius: 12, height: 44, fontWeight: 600 }}
              >
                + Add Passenger
              </Button>
            </div>
          )}
        </Card>

        {/* Submit Section */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 48 }}>
          <Button
            type="primary"
            size="large"
            loading={isSubmitting}
            onClick={handleSubmit}
            style={{
              height: 56,
              borderRadius: 14,
              background: "#ca8a04",
              borderColor: "#ca8a04",
              fontWeight: 700,
              fontSize: 16,
              padding: "0 40px",
              boxShadow: "0 8px 24px rgba(202, 138, 4, 0.25)"
            }}
          >
            Submit Enquiry
          </Button>
        </div>

      </div>

      <style>{`
        .registration-card-page .ant-select-selector {
          border-radius: 12px !important;
          height: 48px !important;
          display: flex;
          align-items: center;
        }
        .registration-card-page .ant-select-selection-item {
          line-height: 48px !important;
        }
        .registration-card-page .ant-select-selection-placeholder {
          line-height: 48px !important;
        }
        .registration-card-page .ant-btn-primary:hover, 
        .registration-card-page .ant-btn-primary:focus {
          background: #b47804 !important;
          border-color: #b47804 !important;
        }
        .registration-card-page .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
          color: #ca8a04 !important;
          border-color: #ca8a04 !important;
          background: #FFFDF2 !important;
        }
        .registration-card-page .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before {
          background-color: #ca8a04 !important;
        }
      `}</style>
    </div>
  );
};

export default RegistrationCard;
